/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */

import React from 'react'
import PropTypes from 'prop-types'
import {useIntl, FormattedMessage} from 'react-intl'
import {
    Box,
    Button,
    Stack,
    Text,
    Divider
} from '@salesforce/retail-react-app/app/components/shared/ui'
import FieldsetForm from '../shared/ui/FieldsetForm'
import {AbsoluteCenter} from '@chakra-ui/react'
import StandardLogin from '../standard-login'
import PasswordlessLogin from '@salesforce/retail-react-app/app/components/passwordless-login'
import {noop} from '../../utils/utils'
import Link from '@salesforce/retail-react-app/app/components/link'
import {TRACK_MY_ORDER_URL} from '../../constants'

const LoginForm = ({
    submitForm,
    handleForgotPasswordClick,
    handlePasswordlessLoginClick,
    clickCreateAccount = noop,
    form,
    isPasswordlessEnabled = false,
    isSocialEnabled = false,
    idps = [],
    setLoginType,
    prefix,
    isFirstInputFocus = false,
    clickTrackOrder = noop
}) => {
    const {formatMessage} = useIntl()

    return (
        <FieldsetForm
            id="login-form"
            data-testid="sf-auth-modal-form"
            legendText={formatMessage({
                defaultMessage: 'Login form',
                id: 'login_form.form.legend'
            })}
            onSubmit={form.handleSubmit(submitForm)}
            error={form.formState.errors?.global?.message}
        >
            {isPasswordlessEnabled ? (
                <PasswordlessLogin
                    form={form}
                    handleForgotPasswordClick={handleForgotPasswordClick}
                    handlePasswordlessLoginClick={handlePasswordlessLoginClick}
                    isSocialEnabled={isSocialEnabled}
                    idps={idps}
                    setLoginType={setLoginType}
                />
            ) : (
                <StandardLogin
                    form={form}
                    handleForgotPasswordClick={handleForgotPasswordClick}
                    isSocialEnabled={isSocialEnabled}
                    idps={idps}
                    prefix={prefix ? prefix : undefined}
                    isFirstInputFocus={isFirstInputFocus}
                />
            )}

            <Box position="relative" px={0} py={6}>
                <Divider color="borderGray" />
                <AbsoluteCenter bg="white" px={12}>
                    <Text variant="descriptor" textTransform="uppercase">
                        <FormattedMessage defaultMessage="or" id="login_form.divider.or" />
                    </Text>
                </AbsoluteCenter>
            </Box>

            <Stack spacing={8} justify="center">
                <Text
                    textAlign="center"
                    variant="bodyLarge2"
                    role="heading"
                    aria-level="2"
                >
                    <FormattedMessage
                        defaultMessage="Don't have an account?"
                        id="login_form.message.dont_have_account"
                    />
                </Text>
                <Button variant="secondary" onClick={clickCreateAccount}>
                    <FormattedMessage
                        defaultMessage="Create account"
                        id="login_form.action.create_account"
                    />
                </Button>
            </Stack>
            <Box textAlign="center">
                <Link to={TRACK_MY_ORDER_URL} onClick={clickTrackOrder}>
                    <FormattedMessage
                        defaultMessage="Track Your Order"
                        id="login_form.link.track_order"
                    />
                </Link>
            </Box>
        </FieldsetForm>
    )
}

LoginForm.propTypes = {
    submitForm: PropTypes.func,
    // clickForgotPassword: PropTypes.func,
    handleForgotPasswordClick: PropTypes.func,
    clickCreateAccount: PropTypes.func,
    handlePasswordlessLoginClick: PropTypes.func,
    form: PropTypes.object,
    isPasswordlessEnabled: PropTypes.bool,
    isSocialEnabled: PropTypes.bool,
    idps: PropTypes.arrayOf(PropTypes.string),
    setLoginType: PropTypes.func,
    prefix: PropTypes.string,
    isFirstInputFocus: PropTypes.bool,
    clickTrackOrder: PropTypes.func
}

export default LoginForm
